import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../http/query-client';
import { AccountSettingsPanel } from './account-settings-panel';
import { Button } from '../../../ui/buttons/button';
import { Trans } from '../../../i18n/trans';
import { showHttpErrorToast } from '../../../utils/http/show-http-error-toast';
import { Form } from '../../../ui/forms/form';
import { useForm } from 'react-hook-form';
import { FormTextField } from '../../../ui/forms/input-field/text-field/text-field';
import { toast } from '../../../ui/toast/toast';
import { User } from '../../user';
import { DialogTrigger } from '../../../ui/overlays/dialog/dialog-trigger';
import { ConfirmPasswordDialog } from './confirm-password-dialog';

export function TwoFactorPanel({ user }: { user: User }) {
  // Try to determine if 2FA is enabled using the standard Fortify attributes, or assume false if unknown
  const [isEnabled, setIsEnabled] = useState(!!(user as any).two_factor_confirmed_at);
  const [showQr, setShowQr] = useState(false);
  const [qrCodeSvg, setQrCodeSvg] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);

  const form = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const enableMutation = useMutation(() => apiClient.post('auth/user/two-factor-authentication'), {
    onSuccess: () => {
      fetchQrCode();
      fetchRecoveryCodes();
      setShowQr(true);
      setIsConfirming(true);
    },
    onError: err => showHttpErrorToast(err),
  });

  const disableMutation = useMutation(() => apiClient.delete('auth/user/two-factor-authentication'), {
    onSuccess: () => {
      setIsEnabled(false);
      setIsConfirming(false);
      setShowQr(false);
      setQrCodeSvg('');
      setRecoveryCodes([]);
      toast('Two-factor authentication disabled');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      toast.danger('Failed to disable: ' + msg);
      showHttpErrorToast(err);
    },
  });

  const confirmMutation = useMutation((code: string) => apiClient.post('auth/user/confirmed-two-factor-authentication', { code }), {
    onSuccess: () => {
      setIsEnabled(true);
      setIsConfirming(false);
      setShowQr(false);
      toast('Two-factor authentication enabled');
    },
    onError: err => showHttpErrorToast(err),
  });

  const fetchQrCode = () => {
    apiClient.get('auth/user/two-factor-qr-code').then(res => {
      setQrCodeSvg(res.data.svg);
    });
  };

  const fetchRecoveryCodes = () => {
    apiClient.get('auth/user/two-factor-recovery-codes').then(res => {
      setRecoveryCodes(res.data);
    });
  };

  const generateNewRecoveryCodes = () => {
    apiClient.post('auth/user/two-factor-recovery-codes').then(() => {
      fetchRecoveryCodes();
    });
  };

  return (
    <AccountSettingsPanel
      title={<Trans message="Two-Factor Authentication (A2F)" />}
      actions={
        isConfirming ? (
          <Button
            variant="flat"
            color="danger"
            disabled={disableMutation.isLoading}
            onClick={() => disableMutation.mutate()}
          >
            <Trans message="Cancel Setup" />
          </Button>
        ) : (
          <DialogTrigger type="modal">
            <Button
              variant="flat"
              color={isEnabled ? 'danger' : 'primary'}
              disabled={enableMutation.isLoading || disableMutation.isLoading}
            >
              {isEnabled ? <Trans message="Disable" /> : <Trans message="Enable" />}
            </Button>
            <ConfirmPasswordDialog onConfirm={() => {
              isEnabled ? disableMutation.mutate() : enableMutation.mutate();
            }} />
          </DialogTrigger>
        )
      }
    >
      <div className="text-base text-muted mb-24">
        {isEnabled ? (
          <Trans message="You have enabled two-factor authentication." />
        ) : (
          <Trans message="You have not enabled two-factor authentication." />
        )}
      </div>

      {showQr && !isEnabled && qrCodeSvg && (
        <div className="mb-24">
          <p className="mb-12 font-medium">
            <Trans message="To finish enabling two-factor authentication, scan the following QR code using your phone's authenticator application or enter the setup key and provide the generated OTP code." />
          </p>
          <div className="bg-white inline-block p-12 rounded" dangerouslySetInnerHTML={{ __html: qrCodeSvg }} />
        </div>
      )}

      {isConfirming && (
        <div className="mb-24">
          <Form
            form={form}
            onSubmit={data => confirmMutation.mutate(data.code)}
          >
            <FormTextField
              name="code"
              label={<Trans message="Code" />}
              className="mb-12 w-1/2"
              required
            />
            <Button type="submit" variant="flat" color="primary" disabled={confirmMutation.isLoading}>
              <Trans message="Confirm" />
            </Button>
          </Form>
        </div>
      )}

      {isEnabled && recoveryCodes.length > 0 && (
        <div className="mb-24">
          <p className="mb-12 font-medium">
            <Trans message="Store these recovery codes in a secure password manager. They can be used to recover access to your account if your two-factor authentication device is lost." />
          </p>
          <div className="bg-alt border p-12 rounded font-mono text-sm mb-12">
            {recoveryCodes.map(code => (
              <div key={code}>{code}</div>
            ))}
          </div>
          <Button variant="outline" onClick={generateNewRecoveryCodes}>
            <Trans message="Regenerate Recovery Codes" />
          </Button>
        </div>
      )}
    </AccountSettingsPanel>
  );
}
